// src/components/Leaderboard.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getDoc, getDocs, query, where, doc, collection } from 'firebase/firestore';
import { firestore, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useSearchParams, useParams } from 'react-router-dom';
import { calculateRank, skyRoundSlotsFromTeamScores } from '../utils/rankUtils';
import SponsorLogos from './SponsorLogos';
import PublicEventChrome from './PublicEventChrome';
import { resolvePublicPageEventId } from '../utils/activeEvent';
import { getEventEditionYear } from '../utils/eventRouting';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import { buildTeamPhotoUrlMap, buildTeamPosterUrlMap, teamPhotoCssObjectPosition } from '../utils/teamPhotoPublic';
import './Leaderboard.css';

const getEventPuzzleMetaFn = httpsCallable(functions, 'getEventPuzzleMeta');

const MOBILE_ROW_LIMIT = 15;

const Leaderboard = () => {
  const [searchParams] = useSearchParams();
  const { year: yearParam } = useParams();
  const eventIdParam = searchParams.get('event');
  const [teams, setTeams] = useState([]);
  const [scores, setScores] = useState([]);
  const [bonusPuzzles, setBonusPuzzles] = useState([]);
  const [bonusScores, setBonusScores] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showMentor, setShowMentor] = useState(false);
  const [showTestTeams, setShowTestTeams] = useState(false);
  // New state for selected view: "full", "beginner", "intermediate", or "sub75"
  const [selectedView, setSelectedView] = useState("full");
  const [sponsors, setSponsors] = useState([]);
  const [mobileShowAll, setMobileShowAll] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [showSkyColumns, setShowSkyColumns] = useState(false);
  const [showBonusColumns, setShowBonusColumns] = useState(false);

  const [memoryTeamMeta, setMemoryTeamMeta] = useState(null);
  /** @type {[null | { teamId: string, headline: string, subtitle: string, imageUrl: string, isPoster: boolean }, function]} */
  const [teamImageModal, setTeamImageModal] = useState(null);

  const teamPhotosPublicMap = useMemo(
    () => buildTeamPhotoUrlMap(memoryTeamMeta),
    [memoryTeamMeta]
  );

  const teamPostersPublicMap = useMemo(
    () => buildTeamPosterUrlMap(memoryTeamMeta),
    [memoryTeamMeta]
  );

  const closeTeamImageModal = useCallback(() => setTeamImageModal(null), []);

  useEffect(() => {
    if (!teamImageModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeTeamImageModal();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [teamImageModal, closeTeamImageModal]);

  const teamHasMemoryBookImage = useCallback(
    (teamId) =>
      !!(teamId && (teamPostersPublicMap[teamId] || teamPhotosPublicMap[teamId])),
    [teamPostersPublicMap, teamPhotosPublicMap]
  );

  const openTeamImageModal = useCallback(
    (team) => {
      const photoUrl = teamPhotosPublicMap[team.id] || '';
      const posterUrl = teamPostersPublicMap[team.id] || '';
      const imageUrl = posterUrl || photoUrl;
      if (!imageUrl) return;
      const teamLabel = team.teamName || `Team ${team.teamNumber}`;
      setTeamImageModal({
        teamId: team.id,
        headline: posterUrl ? 'Team poster' : 'Team photo',
        subtitle:
          team.mentorName && showMentor ? `${teamLabel} · Mentor: ${team.mentorName}` : teamLabel,
        imageUrl,
        isPoster: !!posterUrl,
      });
    },
    [teamPhotosPublicMap, teamPostersPublicMap, showMentor]
  );

  const openFreshlingTeamImageModal = useCallback(
    (f) => {
      const tid = f.teamId;
      if (!tid || !teamHasMemoryBookImage(tid)) return;
      const photoUrl = teamPhotosPublicMap[tid] || '';
      const posterUrl = teamPostersPublicMap[tid] || '';
      const imageUrl = posterUrl || photoUrl;
      const teamLabel = f.teamName || `Team ${f.teamNumber}`;
      setTeamImageModal({
        teamId: tid,
        headline: posterUrl ? 'Team poster' : 'Team photo',
        subtitle: teamLabel,
        imageUrl,
        isPoster: !!posterUrl,
      });
    },
    [teamPhotosPublicMap, teamPostersPublicMap, teamHasMemoryBookImage]
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    setMobileShowAll(false);
  }, [selectedView]);

  useEffect(() => {
    const fetchData = async () => {
      const id = await resolvePublicPageEventId({
        eventIdParam,
        editionYearParam: yearParam,
      });
      if (!id) return;
      const eventSnap = await getDoc(doc(firestore, 'events', id));
      if (!eventSnap.exists()) return;
      const event = { id: eventSnap.id, ...eventSnap.data() };
      setActiveEvent(event);

      const metaSnap = await getDoc(doc(firestore, 'eventMemoryMeta', event.id));
      setMemoryTeamMeta(metaSnap.exists() ? metaSnap.data() : null);

      // Get teams (each team record should include a "freshlings" array of email addresses)
      const teamQuery = query(
        collection(firestore, 'teams'),
        where('eventId', '==', event.id)
      );
      const teamSnap = await getDocs(teamQuery);
      const allTeams = teamSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(team => !!team.mentor);
      setTeams(allTeams);

      // Get scores, bonus puzzles (via Cloud Function), and bonus scores in parallel
      const [scoreSnap, puzzleResult, bonusScoreSnap] = await Promise.all([
        getDocs(query(collection(firestore, 'scores'), where('eventId', '==', event.id))),
        getEventPuzzleMetaFn({eventId: event.id}),
        getDocs(query(collection(firestore, 'bonusScores'), where('eventId', '==', event.id))),
      ]);
      setScores(scoreSnap.docs.map(doc => doc.data()));
      setBonusPuzzles(puzzleResult.data.puzzles || []);
      setBonusScores(bonusScoreSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const sponsorListRaw = await fetchSponsorsMergedForEvent(event.id);
      const sponsorList = sponsorListRaw
        .filter(s => !s.hidden && (s.type || 'none') !== 'none');
      sponsorList.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setSponsors(sponsorList);
    };

    fetchData();
  }, [eventIdParam, yearParam]);

  // Helper: Format a freshling's displayName as "First L." if not already abbreviated.
  const formatFreshlingName = (displayName) => {
    if (!displayName) return '';
    return displayName;
  };

  // Abbreviate division for compact display (especially on mobile)
  const divisionLabel = (division) => {
    if (!division || !String(division).trim()) return '?';
    const d = String(division).toLowerCase();
    if (d === 'unassigned') return '?';
    if (d === 'beginner') return 'Beg.';
    if (d === 'intermediate') return 'Inter.';
    return division;
  };

  const nonTestTeams = teams.filter((t) => !t.test);
  const testTeams = teams.filter((t) => t.test);

  // Build full leaderboard data for teams using your calculateRank tie-breaker logic.
  const buildLeaderboard = (teamList) => {
    return (teamList || teams).map(team => {
      // Get team scores and sort the non-bonus scores by round
      const teamScores = scores.filter(s => s.teamId === team.id);
      const { numericSkyScores, skyScores } = skyRoundSlotsFromTeamScores(teamScores);
      const skySubtotal = numericSkyScores.reduce((sum, val) => sum + val, 0);
      const teamBonusScores = bonusPuzzles.map(puzzle => {
        const score = bonusScores.find(b => b.teamId === team.id && b.puzzleId === puzzle.id);
        return score ? score.value : 0;
      });
      const bonusSubtotal = teamBonusScores.reduce((sum, val) => sum + val, 0);
      const total = skySubtotal + bonusSubtotal;
      return {
        id: team.id,
        teamNumber: team.teamNumber || '',
        teamName: team.teamName || '',
        mentorName: team.mentorDisplayName || '',
        division: team.division || 'Unassigned',
        numericSkyScores,
        skyScores,
        skySubtotal,
        bonusScores: teamBonusScores,
        bonusSubtotal,
        total,
        freshlingsInfo: team.freshlingsInfo || [],
      };
    });
  };

  // Compute full leaderboard data and assign ranks (non-test teams only for official ranking).
  const fullLeaderboardData = calculateRank(buildLeaderboard(nonTestTeams), scores, bonusScores, bonusPuzzles);

  // Optional: leaderboard rows for test teams (no ranks; for display when showTestTeams is on).
  const testLeaderboardData = testTeams.length > 0 ? buildLeaderboard(testTeams) : [];

  // For team views, filter based on selectedView.
  let teamViewData = [];
  if (selectedView === "full") {
    teamViewData = [...fullLeaderboardData].sort((a, b) => a.rank - b.rank);
  } else if (selectedView === "beginner") {
    teamViewData = fullLeaderboardData.filter(team => team.division.toLowerCase() === "beginner");
    teamViewData = calculateRank(teamViewData, scores, bonusScores, bonusPuzzles).sort((a, b) => a.rank - b.rank);
  } else if (selectedView === "intermediate") {
    teamViewData = fullLeaderboardData.filter(team => team.division.toLowerCase() === "intermediate");
    teamViewData = calculateRank(teamViewData, scores, bonusScores, bonusPuzzles).sort((a, b) => a.rank - b.rank);
  }

  // Render the team leaderboard table (with optional Sky/Bonus detail columns).
  const renderTeamTable = (data) => {
    const showSkyDetail = showSkyColumns;
    const showBonusDetail = showBonusColumns;
    return (
      <div className="table-container">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th rowSpan={showSkyDetail || showBonusDetail ? 2 : 1} className="narrow">Rank</th>
              <th rowSpan={showSkyDetail || showBonusDetail ? 2 : 1} className="narrow sticky-col-name">{showMentor ? 'Mentor Name' : 'Team Name'}</th>
              <th rowSpan={showSkyDetail || showBonusDetail ? 2 : 1} className="narrow">Team</th>
              <th rowSpan={showSkyDetail || showBonusDetail ? 2 : 1} className="narrow">Total</th>
              <th rowSpan={showSkyDetail || showBonusDetail ? 2 : 1} className="narrow">Div.</th>
              {showSkyDetail ? (
                <th
                  colSpan="5"
                  className="narrow header-toggle-cell"
                  onClick={() => setShowSkyColumns(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowSkyColumns(false)}
                  role="button"
                  tabIndex={0}
                  title="Click to collapse"
                >
                  Sky ▼
                </th>
              ) : (
                <th
                  rowSpan="2"
                  className="narrow header-toggle-cell"
                  onClick={() => setShowSkyColumns(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowSkyColumns(true)}
                  role="button"
                  tabIndex={0}
                  title="Click to expand"
                >
                  Sky ∑ ▶
                </th>
              )}
              {showBonusDetail ? (
                <th
                  colSpan={bonusPuzzles.length + 1}
                  className="header-toggle-cell"
                  onClick={() => setShowBonusColumns(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowBonusColumns(false)}
                  role="button"
                  tabIndex={0}
                  title="Click to collapse"
                >
                  Bonus ▼
                </th>
              ) : (
                <th
                  rowSpan="2"
                  className="narrow header-toggle-cell"
                  onClick={() => setShowBonusColumns(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowBonusColumns(true)}
                  role="button"
                  tabIndex={0}
                  title="Click to expand"
                >
                  Bonus ∑ ▶
                </th>
              )}
            </tr>
            {(showSkyDetail || showBonusDetail) && (
              <tr>
                {showSkyDetail && (
                  <>
                    {[1, 2, 3, 4].map(n => (
                      <th key={`sky-${n}`} className="narrow">{n}</th>
                    ))}
                    <th className="narrow">∑</th>
                  </>
                )}
                {showBonusDetail && (
                  <>
                    {bonusPuzzles.map(p => (
                      <th key={p.id} className="tight">{p.number}</th>
                    ))}
                    <th className="tight">∑</th>
                  </>
                )}
              </tr>
            )}
          </thead>
            <tbody>
              {data.map((team) => {
                const media = teamHasMemoryBookImage(team.id);
                const teamLabel = team.teamName || `Team ${team.teamNumber}`;
                const nameCellInner = !showMentor ? (
                  media ? (
                    <button
                      type="button"
                      className="leaderboard-team-media-link"
                      onClick={() => openTeamImageModal(team)}
                    >
                      {teamLabel}
                    </button>
                  ) : (
                    teamLabel
                  )
                ) : (
                  <>
                    <span>{team.mentorName}</span>
                    {media ? (
                      <>
                        {' · '}
                        <button
                          type="button"
                          className="leaderboard-team-media-link"
                          onClick={() => openTeamImageModal(team)}
                          aria-label={`View team poster or photo · ${teamLabel}`}
                        >
                          {teamLabel}
                        </button>
                      </>
                    ) : null}
                  </>
                );

                return (
                  <tr key={team.id}>
                    <td className="narrow">{team.rank}</td>
                    <td className="narrow sticky-col-name">{nameCellInner}</td>
                    <td className="narrow">{team.teamNumber}</td>
                    <td className="narrow">{team.total}</td>
                    <td className="narrow">{divisionLabel(team.division)}</td>
                    {showSkyDetail ? (
                      <>
                        {team.skyScores.map((score, i) => (
                          <td key={`sky-${i}`} className="narrow">{score}</td>
                        ))}
                        <td className="narrow">{team.skySubtotal}</td>
                      </>
                    ) : (
                      <td className="narrow">{team.skySubtotal}</td>
                    )}
                    {showBonusDetail ? (
                      <>
                        {team.bonusScores.map((score, i) => (
                          <td key={`bonus-${i}`} className={`tight ${score > 0 ? 'highlight-bonus' : ''}`}>
                            {score === 0 ? '' : score}
                          </td>
                        ))}
                        <td className="tight">{team.bonusSubtotal}</td>
                      </>
                    ) : (
                      <td className="narrow">{team.bonusSubtotal}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
    );
  };

  // Build the Freshlings Leaderboard from denormalized team.freshlingsInfo.
  // Include only those with jumpNumber <= 75.
  const buildFreshlingsLeaderboard = (fullLeaderboardData) => {
    const freshlingsList = [];
    fullLeaderboardData.forEach(team => {
      if (Array.isArray(team.freshlingsInfo)) {
        team.freshlingsInfo.forEach(fi => {
          const jumps = Number(fi.jumpNumber || 0);
          if (jumps <= 75) {
            freshlingsList.push({
              teamId: team.id,
              freshlingName: fi.displayName || '',
              jumpNumber: jumps,
              totalScore: team.total,
              teamName: team.teamName,
              teamNumber: team.teamNumber,
              rank: team.rank,
            });
          }
        });
      }
    });
    return freshlingsList.sort((a, b) => a.rank - b.rank);
  };

  const rankedFreshlings = buildFreshlingsLeaderboard(fullLeaderboardData);

  // Render freshlings table
  const renderFreshlingsTable = (data) => {
    return (
      <div className="table-container">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th className="narrow">Rank</th>
              <th className="narrow">Team Name</th>
              <th className="narrow">Name</th>
              <th className="narrow">Jumps</th>
              <th className="narrow">Total Score</th>
              <th className="narrow">Team Number</th>
            </tr>
          </thead>
          <tbody>
            {data.map((f, i) => {
              const tLabel = f.teamName || `Team ${f.teamNumber}`;
              return (
              <tr key={`${f.teamNumber}-${i}`}>
                <td className="narrow">{i + 1}</td>
                <td className="narrow">
                  {f.teamId && teamHasMemoryBookImage(f.teamId) ? (
                    <button
                      type="button"
                      className="leaderboard-team-media-link"
                      onClick={() => openFreshlingTeamImageModal(f)}
                    >
                      {tLabel}
                    </button>
                  ) : (
                    tLabel
                  )}
                </td>
                <td className="narrow">{formatFreshlingName(f.freshlingName)}</td>
                <td className="narrow">{f.jumpNumber}</td>
                <td className="narrow">{f.totalScore}</td>
                <td className="narrow">{f.teamNumber}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // On mobile, show only first N rows by default to keep the page short.
  const limitRows = isMobile && !mobileShowAll;
  const teamDataToShow = limitRows ? teamViewData.slice(0, MOBILE_ROW_LIMIT) : teamViewData;
  const freshlingsDataToShow = limitRows ? rankedFreshlings.slice(0, MOBILE_ROW_LIMIT) : rankedFreshlings;
  const teamTotalCount = teamViewData.length;
  const freshlingsTotalCount = rankedFreshlings.length;
  const showExpandButton =
    isMobile &&
    !mobileShowAll &&
    (selectedView === "sub75" ? freshlingsTotalCount > MOBILE_ROW_LIMIT : teamTotalCount > MOBILE_ROW_LIMIT);

  // Decide what to render based on selectedView.
  let viewContent = null;
  if (selectedView === "sub75") {
    viewContent = renderFreshlingsTable(freshlingsDataToShow);
  } else {
    viewContent = renderTeamTable(teamDataToShow);
  }

  const eventQueryId = (eventIdParam || activeEvent?.id || '').toString().trim();

  return (
    <div className="leaderboard">
      <PublicEventChrome
        activeNav="leaderboard"
        eventQueryId={eventQueryId}
        editionYearParam={yearParam}
        editionYearFallback={activeEvent ? getEventEditionYear(activeEvent) : null}
      />
      <header className="header-container header-container--below-chrome">
        <h2 className="header-title">{activeEvent?.title || 'Leaderboard'}</h2>
        <div className="header-toggle">
          <label className="mentor-toggle">
            <input
              type="checkbox"
              checked={showMentor}
              onChange={() => setShowMentor(!showMentor)}
            />
            Show Mentors
          </label>
          <label className="test-teams-toggle">
            <input
              type="checkbox"
              checked={showTestTeams}
              onChange={() => setShowTestTeams(!showTestTeams)}
            />
            Show test teams
          </label>
        </div>
      </header>

      {/* Centered Buttons to Switch Views */}
      <div className="leaderboard-buttons" style={{ textAlign: 'center', margin: '1rem 0' }}>
        <button
          onClick={() => setSelectedView("beginner")}
          className={selectedView === "beginner" ? "active" : ""}
        >
          {isMobile ? 'Beg' : 'Beginner'}
        </button>
        <button
          onClick={() => setSelectedView("intermediate")}
          className={selectedView === "intermediate" ? "active" : ""}
        >
          {isMobile ? 'Inter' : 'Intermediate'}
        </button>
        <button
          onClick={() => setSelectedView("sub75")}
          className={selectedView === "sub75" ? "active" : ""}
        >
          Sub 75
        </button>
        <button
          onClick={() => setSelectedView("full")}
          className={selectedView === "full" ? "active" : ""}
        >
          Full
        </button>
      </div>

      {/* Render the Selected View */}
      {viewContent}

      {showExpandButton && (
        <div className="leaderboard-show-all-wrap">
          <button
            type="button"
            className="leaderboard-show-all-btn btn-fm-primary"
            onClick={() => setMobileShowAll(true)}
          >
            Show all {selectedView === "sub75" ? freshlingsTotalCount : teamTotalCount}{' '}
            {selectedView === "sub75" ? 'freshlings' : 'teams'}
          </button>
        </div>
      )}

      {/* Test teams section (excluded from official ranking) */}
      {showTestTeams && testLeaderboardData.length > 0 && selectedView !== "sub75" && (
        <div className="leaderboard-test-section">
          <h3>Test teams (not ranked)</h3>
          {renderTeamTable(
            testLeaderboardData.map((row) => ({ ...row, rank: '—' }))
          )}
        </div>
      )}

      {teamImageModal && (
        <div
          className="leaderboard-team-photo-modal-root"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leaderboard-photo-modal-title"
        >
          <button
            type="button"
            className="leaderboard-team-photo-modal-backdrop"
            aria-label="Close"
            onClick={closeTeamImageModal}
          />
          <div className="leaderboard-team-photo-modal-panel">
            <div className="leaderboard-team-photo-modal-toolbar">
              <h3 id="leaderboard-photo-modal-title" className="leaderboard-team-photo-modal-title">
                {teamImageModal.headline}
              </h3>
              <button
                type="button"
                className="leaderboard-team-photo-modal-close"
                onClick={closeTeamImageModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="leaderboard-team-photo-modal-sub">{teamImageModal.subtitle}</p>
            <div className={`leaderboard-team-photo-modal-img-wrap${teamImageModal.isPoster ? ' leaderboard-team-photo-modal-img-wrap--poster' : ''}`}>
              <img
                src={teamImageModal.imageUrl}
                alt=""
                className="leaderboard-team-photo-modal-img"
                style={
                  teamImageModal.isPoster
                    ? undefined
                    : {
                        objectPosition: teamPhotoCssObjectPosition(teamImageModal.teamId, memoryTeamMeta),
                      }
                }
              />
            </div>
          </div>
        </div>
      )}

      {sponsors.length > 0 && <SponsorLogos sponsors={sponsors} variant="subtle" />}
      {/* Tie-breaker Explanation */}
      <div className="tie-breaker-info">
        <small>
          <strong>Tie-Breaker Explanation:</strong> Teams are ranked by total score (sky + bonus). If tied, the sky subtotal is compared, and if still tied, the rounds are compared in reverse order.
        </small>
      </div>
    </div>
  );
};

export default Leaderboard;
